import { memo, useMemo } from 'react';
import { TreeListCellProps } from '@progress/kendo-react-treelist';
import ClickableDiv from 'common/components/clickableDiv/ClickableDiv';
import { KWELLO_ICON, KwelloSvgIcon } from '../../icon/KwelloSvgIcon';
import {
  Sparkline,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartArea,
  ValueAxis,
  ChartTooltip,
} from '@progress/kendo-react-charts';
import { Button } from '@progress/kendo-react-buttons';
import { AppLink } from '../../navigation/AppLink';
import { showNumberWithSign } from '../utils';

import {
  GridCellProps,
  GridColumnMenuSort,
  GridColumnMenuFilter,
} from '@progress/kendo-react-grid';

import { Tooltip } from 'react-tooltip';

import { formatAppDate, percentage, sanatizeString } from 'common/hooks/utils';
import { ALIGNMENT_ROUTES, INSIGHTS_ROUTES } from 'routes/routes.const';
import { uniqueString } from '../widget-utils/widgetUtils';

export const ColumnMenu = (props: any) => {
  return (
    <div>
      {props.sortable !== false && <GridColumnMenuSort {...props} />}
      {props.filterable !== false && <GridColumnMenuFilter {...props} />}
    </div>
  );
};

export const StatusCell = (props: TreeListCellProps) => {
  if (props.dataItem.footer) {
    return <td className="cell-total cell-footer">Total:</td>;
  }
  if (props.level.length == 1) {
    if (props.dataItem.isActive) {
      return (
        <td className="cell-isactive">
          <div className="label-active">
            {KwelloSvgIcon({
              icon: KWELLO_ICON.UNDERLINED_TICK_ICON,
              className: 'k-svg-icon',
            })}
            Active
          </div>
        </td>
      );
    } else {
      return (
        <td className="cell-isexpired">
          <div className="label-expired">
            {KwelloSvgIcon({
              icon: KWELLO_ICON.CROSSED_EYE_ICON,
              className: 'k-svg-icon',
            })}
            Expired
          </div>
        </td>
      );
    }
  } else {
    return <td></td>;
  }
};

export const DateCell = (date: string) => {
  const dateString =
    date !== null && typeof date != 'undefined'
      ? formatAppDate(new Date(date), 'dd MMM yy')
      : 'N/A';
  return (
    <td className="cell-sparkline">
      <div className="spark-line-box alignment-box">
        <p className="spark-line-value alignment-value">{dateString}</p>
      </div>
    </td>
  );
};

export const NumberCell = (number: number | string) => {
  return (
    <td className="cell-sparkline">
      <div className="spark-line-box alignment-box">
        <p className="spark-line-value alignment-value">{number}</p>
      </div>
    </td>
  );
};

export const AlignmentTextCell = (
  alignmentScore: number,
  changeScore: number = 0,
  backgroundColor?: string,
  textColor?: string,
  label?: string
) => {
  if (alignmentScore === undefined) {
    return <td className="cell-sparkline"></td>;
  }

  return (
    <td className="cell-sparkline">
      <div
        className="spark-line-box alignment-box alignment-state-cell"
        style={{
          backgroundColor: backgroundColor,
        }}
      >
        <div>
          <p className="spark-line-value alignment-value" style={{ color: textColor }}>
            {label}
          </p>
        </div>
        {changeScore > 0 && (
          <KwelloSvgIcon
            icon={KWELLO_ICON.UP_ARROW}
            className="k-icon k-svg-icon k-button-icon marginRight5 fillGreenColorIcon"
          />
        )}
        {changeScore < 0 && (
          <KwelloSvgIcon
            icon={KWELLO_ICON.DOWN_ARROW}
            className="k-icon k-svg-icon k-button-icon fillRedColorIcon"
          />
        )}
      </div>
    </td>
  );
};

export const AlignmentChangeCell = (props: TreeListCellProps | GridCellProps | undefined) => {
  if (!props) {
    return <td></td>;
  }

  const change: number = props.dataItem['change'] || 0;
  if (change === 0) {
    return (
      <td>
        <p className="nochange">No Change</p>
      </td>
    );
  }
  const min = -5;
  const max = 5;

  const PositiveSparklineProps: ValueAxis = {
    min,
    max,
    plotBands: [
      { from: min, to: 0, color: '#787878', opacity: 0.15 },
      { from: 0, to: Math.min(change, max), color: 'green' },
      { from: Math.min(change, max), to: max, color: '#787878', opacity: 0.15 },
    ],
  };

  const NegativeSparklineProps: ValueAxis = {
    min,
    max,
    plotBands: [
      { from: min, to: Math.max(change, min), color: '#787878', opacity: 0.15 },
      { from: Math.max(change, min), to: 0, color: 'red' },
      { from: 0, to: max, color: '#787878', opacity: 0.15 },
    ],
  };

  const axisProps = change < 0 ? NegativeSparklineProps : PositiveSparklineProps;

  return (
    <td>
      <div className={change > 0 ? 'positive' : 'negative'}>{showNumberWithSign(change)}</div>
      <div className="centered-flex">
        <Sparkline data={[0]} type="bullet">
          <ChartArea width={160} height={10} />
          <ChartTooltip visible={false} />
          {change != 0 && (
            <ChartValueAxis>
              <ChartValueAxisItem {...axisProps} />
            </ChartValueAxis>
          )}
        </Sparkline>
      </div>
    </td>
  );
};

export const SparkLineTooltip = ({
  blueLineValue,
  orangeLineValue,
  blueLineLabel,
  orangeLineLabel,
  blackLineValue,
  blackLineLabel,
}: {
  blueLineValue?: string;
  orangeLineValue?: string;
  blueLineLabel?: string;
  orangeLineLabel?: string;
  blackLineValue?: string;
  blackLineLabel?: string;
}) => {
  return (
    <div className="sparkline-tooltip">
      <div className="sparkline-tooltip-indicators">
        {blueLineValue !== undefined && (
          <div className="sparkline-tooltip-indicators-circle blue" />
        )}
        {orangeLineValue !== undefined && (
          <div className="sparkline-tooltip-indicators-circle orange" />
        )}
        {blackLineValue !== undefined && (
          <div className="sparkline-tooltip-indicators-circle black" />
        )}
      </div>
      <div className="sparkline-tooltip-text">
        {blueLineLabel && <div>{blueLineLabel}</div>}
        {orangeLineLabel && <div>{orangeLineLabel}</div>}
        {blackLineLabel && <div>{blackLineLabel}</div>}
      </div>
      <div className="sparkline-tooltip-values">
        {blueLineValue !== undefined && (
          <div className="sparkline-tooltip-values-item">{blueLineValue}</div>
        )}
        {orangeLineValue !== undefined && (
          <div className="sparkline-tooltip-values-item">{orangeLineValue}</div>
        )}
        {blackLineValue !== undefined && (
          <div className="sparkline-tooltip-values-item">{blackLineValue}</div>
        )}
      </div>
    </div>
  );
};

interface QuestionCellProps extends TreeListCellProps {
  detailUrl?: 'alignment' | 'insights';
}

export const QuestionsCell = (props: QuestionCellProps) => {
  const { detailUrl = 'insights' } = props;

  const url = detailUrl === 'alignment' ? ALIGNMENT_ROUTES.detail : INSIGHTS_ROUTES.detail;

  if (props.dataItem.questions && !props.dataItem.footer) {
    return <td className="cell-questions">{props.dataItem.questions.length}</td>;
  } else if (!props.dataItem.footer) {
    return (
      <td className="cell-action">
        <AppLink to={`${url}?question_id=` + props.dataItem?.id}>
          <Button
            rounded="large"
            fillMode={'solid'}
            size={'small'}
            className="k-button-action"
            title={props.dataItem?.question}
          >
            Detail
            <KwelloSvgIcon
              icon={KWELLO_ICON.ARROW_RIGHT}
              className="k-icon k-svg-icon k-button-icon"
            />
          </Button>
        </AppLink>
      </td>
    );
  }
  return <td></td>;
};

interface FractionBarCellProps {
  total?: number;
  labelContent?: (value: number) => string;
  lineColor?: string;
  fieldValue?: number;
  footerTotal?: number | null;
  showTooltips?: boolean;
}

interface FractionBarVisibleFeatureFlag {
  showFractionBars: boolean;
}

export const FeatureFlaggedFractionCell = (
  props: FractionBarCellProps & FractionBarVisibleFeatureFlag
) => {
  const { showFractionBars, fieldValue } = props;
  if (showFractionBars) {
    return <FractionBarCell {...props} />;
  }

  if (props.footerTotal) {
    return <td>{props.footerTotal}</td>;
  }

  return <td>{fieldValue?.toString() ?? ''}</td>;
};

export const FractionBarCell = memo(
  ({
    total,
    labelContent,
    lineColor,
    fieldValue,
    footerTotal,
    showTooltips,
  }: FractionBarCellProps) => {
    const response = fieldValue ?? 0;

    const label: string | number = useMemo(
      (): string | number => (labelContent ? labelContent(response) : response),
      [response, labelContent]
    );
    const progressBarTotal = total ?? response;
    if (footerTotal) {
      return <td className="cell-footer">{footerTotal}</td>;
    }
    const progressBarWidth = response === 0 ? 0 : percentage(response, progressBarTotal, 'ceiled');
    const randomTooltipId = uniqueString();
    const tooltipId = `fraction-cell-tooltip-${randomTooltipId}`;
    return (
      <td
        className="cell-sparkline"
        data-tooltip-id={tooltipId}
        data-tooltip-content={`${progressBarWidth}%`}
        data-tooltip-place="top"
      >
        {showTooltips === true && <Tooltip id={tooltipId} />}
        <div className="spark-line-box">
          <p className="spark-line-value">{label}</p>
          <div
            className="cell-progressBar"
            style={{ width: Math.ceil(progressBarWidth) + '%', backgroundColor: lineColor }}
          ></div>
        </div>
      </td>
    );
  }
);

export const AggregateIntegersCell = (props: TreeListCellProps, aggregate: string) => {
  const response = props.dataItem.aggregates[aggregate];
  return (
    <td className="cell-sparkline">
      <div className="spark-line-box">
        <p className="spark-line-value">{response}</p>
      </div>
    </td>
  );
};

export const DetailBtnCell = (props: TreeListCellProps) => {
  return (
    <td className="cell-action">
      <AppLink to={`${ALIGNMENT_ROUTES.detail}?question_id=` + props.dataItem?.id}>
        <Button rounded="large" fillMode={'solid'} size={'small'} className="k-button-action">
          Detail
          <KwelloSvgIcon
            icon={KWELLO_ICON.ARROW_RIGHT}
            className="k-icon k-svg-icon k-button-icon"
          />
        </Button>
      </AppLink>
    </td>
  );
};

export const AlignmentCell = (props: TreeListCellProps) => {
  const response = props.dataItem.aggregates['AlignmentScore'];
  return (
    <td className="cell-sparkline">
      <div
        className="spark-line-box alignment-box"
        style={
          {
            // backgroundColor: props.dataItem.alignmentColor1,
          }
        }
      >
        <p className="spark-line-value alignment-value">{response ? response.toFixed(2) : ''}</p>
      </div>
    </td>
  );
};

export const AlignmentCell2 = (props: TreeListCellProps | GridCellProps | undefined) => {
  const response = props?.dataItem.alignment;
  return (
    <td className="cell-sparkline">
      <div
        className="spark-line-box alignment-box"
        style={
          {
            // backgroundColor: props?.dataItem.alignmentColor1,
            // color: props?.dataItem.alignmentColor2,
          }
        }
      >
        <p className="spark-line-value alignment-value">
          {!isNaN(response) ? response.toFixed(2) : ''}
        </p>
      </div>
    </td>
  );
};

export const ClickableFilterCell = ({
  type,
  displayValue,
  value,
  itemId,
  onDataSliceClick,
  showTooltip,
}: {
  type: string;
  displayValue?: string;
  value: string;
  itemId: string;
  onDataSliceClick?: (prop: string, value: string | number, displayValue?: string | number) => void;
  showTooltip?: boolean;
}): JSX.Element => {
  const sanitizedValue = sanatizeString(value);

  const onClick = () => {
    if (onDataSliceClick) {
      onDataSliceClick(type, value, displayValue);
    }
  };
  return (
    <td className="clickable-treelist-cell">
      <ClickableDiv className="row" onClick={() => onClick()}>
        <p>{displayValue ?? value}</p>
        {showTooltip && (
          <>
            <span data-tooltip-id={'info-' + sanitizedValue + '-' + itemId}>
              <KwelloSvgIcon icon={KWELLO_ICON.INFO_ROUNDED_ICON} />
            </span>
            <Tooltip
              id={'info-' + sanitizedValue + '-' + itemId}
              place="top"
              positionStrategy={'fixed'}
              style={{ zIndex: 9999 }}
            >
              <div style={{ maxWidth: 300 }}>{value}</div>
            </Tooltip>
          </>
        )}
      </ClickableDiv>
    </td>
  );
};
